import classNames from "classnames";
import { Entry } from "contentful";
import Link from "next/link";
import { useRouter } from "next/router";
import React from "react";

import Editorials from "~/types/editorials";
import { assertEditorialType, isActiveEntry } from "~/utils/editorial-utils";
import textUtils from "~/utils/text-utils";

import Icon from "../common/icon";
import MediaAsset from "../common/media-asset";
import styles from "./product-navigation-items-current-path.module.scss";

type MediaLinksProps = {
  item: Entry<Editorials.Link>;
  media: Entry<Editorials.Asset>;
  isLastElement: boolean;
};

export function MediaLinks({ item, media, isLastElement }: MediaLinksProps) {
  const router = useRouter();
  return (
    <div className={classNames(styles.wrapperLink, styles.media)}>
      <Link href={textUtils.sanitizeContentfulUrl(item, router)} className={styles.link}>
        <div className={styles.wrapperMedia}>
          <MediaAsset entry={media} />
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 50 50" fill="none">
            <circle
              className={styles.circle}
              opacity="0.5"
              cx="50%"
              cy="50%"
              r="24"
              stroke="url(#paint0"
              strokeWidth="2"
            />
            <defs>
              <linearGradient
                id="paint0"
                x1="1.58249"
                y1="0.999815"
                x2="45.3495"
                y2="1.04954"
                gradientUnits="userSpaceOnUse"
              >
                <stop offset="0.157279" stopColor="#DB5C00" />
                <stop offset="1" stopColor="#E787B5" />
              </linearGradient>
            </defs>
          </svg>
        </div>
        <span className={styles.text}>{item.fields.text}</span>
      </Link>
      {isLastElement ? <Icon name="inclined-star" height={6} /> : null}
    </div>
  );
}

type LinksProps = {
  items?: Entry<Editorials.Link | Editorials.MenuGroup>[];
  changeHeaderLayout?: boolean;
};

export function GetLinks({ items, changeHeaderLayout }: LinksProps) {
  const router = useRouter();
  return (
    <div className={classNames(styles.containerLinks, changeHeaderLayout ? styles.changeHeaderLayout : undefined)}>
      {items && items.length > 0 ? (
        <>
          {items.filter(isActiveEntry).map((item, i) => {
            switch (item.sys.contentType.sys.id) {
              case "link":
                if (!isActiveEntry(item)) return null;
                assertEditorialType<Editorials.Link>(item, "link");

                if (item.fields.image) {
                  return (
                    <MediaLinks
                      key={`${item.sys.id}`}
                      item={item}
                      media={item.fields.image}
                      isLastElement={i < items?.filter(isActiveEntry).length - 1}
                    />
                  );
                }

                return (
                  <div key={`${item.sys.id}`} className={styles.wrapperLink}>
                    <Link href={textUtils.sanitizeContentfulUrl(item, router)}>{item.fields.text}</Link>
                  </div>
                );
              case "menuGroup":
                if (!isActiveEntry(item)) return null;
                assertEditorialType<Editorials.MenuGroup>(item, "menuGroup");

                if (item.fields.link.fields?.image) {
                  return (
                    <MediaLinks
                      key={`${item.sys.id}`}
                      item={item.fields.link}
                      media={item.fields.link.fields.image}
                      isLastElement={i < items?.filter(isActiveEntry).length - 1}
                    />
                  );
                }

                return (
                  <div key={`${item.sys.id}`} className={styles.wrapperLink}>
                    {isActiveEntry(item.fields.link) ? (
                      <Link href={textUtils.sanitizeContentfulUrl(item.fields.link, router)}>
                        {item.fields.link.fields.text}
                      </Link>
                    ) : null}
                  </div>
                );
              default:
                return null;
            }
          })}
        </>
      ) : null}
    </div>
  );
}

type Props = {
  navigation: Entry<unknown>;
  listLinks?: Entry<Editorials.Link>[];
  changeHeaderLayout?: boolean;
};

//TODO: refactor this disgusting component
export default function ProductNavigationItemsCurrentPath({ navigation, listLinks, changeHeaderLayout }: Props) {
  const router = useRouter();
  const path = "/c/" + (router.query.category as string[]).join("/") + "/";
  listLinks = listLinks?.filter(isActiveEntry);

  let item: Entry<Editorials.NavigationItemStandard | Editorials.MenuGroup> | undefined;
  assertEditorialType<Editorials.Nav>(navigation, "navigation");

  const navigationItems = navigation.fields.navigationItems
    ?.filter(isActiveEntry)
    .filter((navItem) => navItem.sys.contentType.sys.id === "navigationItemStandard");

  //this function gets all the children of a category, based on the url
  //Note: if categories increase to 5 or more, change this function to recursive
  navigationItems?.forEach((navigationItemStandard) => {
    assertEditorialType<Editorials.NavigationItemStandard>(navigationItemStandard, "navigationItemStandard");
    if (item || !isActiveEntry(navigationItemStandard.fields.link)) {
      return;
    }

    if (textUtils.sanitizeContentfulUrl(navigationItemStandard.fields.link, router) === path) {
      item = navigationItemStandard;
    } else {
      if (!isActiveEntry(navigationItemStandard.fields.menu!)) return null;

      navigationItemStandard.fields.menu?.fields.main.forEach((menu) => {
        if (item || !isActiveEntry(menu)) return;
        if (menu.sys.contentType.sys.id === "menuGroup") {
          assertEditorialType<Editorials.MenuGroup>(menu, "menuGroup");
          if (!isActiveEntry(menu.fields.link)) return null;
          if (textUtils.sanitizeContentfulUrl(menu.fields.link, router) === path) item = menu;
        } else {
          assertEditorialType<Editorials.MenuColumn>(menu, "menuColumn");
          item = menu.fields.items?.filter(isActiveEntry).find((menuGroup) => {
            if (!isActiveEntry(menuGroup.fields.link)) return;
            return textUtils.sanitizeContentfulUrl(menuGroup.fields.link, router) === path;
          });
        }
        menu.fields.items?.filter(isActiveEntry).forEach((menu) => {
          if (item) return;
          if (menu.sys.contentType.sys.id === "menuGroup") {
            assertEditorialType<Editorials.MenuGroup>(menu, "menuGroup");
            if (!isActiveEntry(menu.fields.link)) return;
            if (textUtils.sanitizeContentfulUrl(menu.fields.link, router) === path) item = menu;
          }
        });
      });
    }
  });

  switch (item?.sys.contentType.sys.id) {
    case "navigationItemStandard":
      assertEditorialType<Editorials.NavigationItemStandard>(item, "navigationItemStandard");

      if (!isActiveEntry(item?.fields.menu!))
        return <>{listLinks ? <GetLinks items={listLinks} changeHeaderLayout={changeHeaderLayout} /> : null}</>;

      const menuItems = item?.fields.menu?.fields.main.flatMap((menu) => {
        if (menu.sys.contentType?.sys.id === "menuGroup") {
          if (!isActiveEntry(menu)) return;
          assertEditorialType<Editorials.MenuGroup>(menu, "menuGroup");
          if (!isActiveEntry(menu.fields.link)) return;
          return menu.fields.link;
        } else {
          if (!isActiveEntry(menu)) return;
          assertEditorialType<Editorials.MenuColumn>(menu, "menuColumn");

          return [...menu.fields.items?.filter(isActiveEntry)].map((menuGroup) => {
            if (!isActiveEntry(menuGroup.fields.link)) return;
            return menuGroup.fields.link;
          });
        }
      });

      const menuItemsFiltered = menuItems?.filter((item) => item !== undefined) as Entry<Editorials.Link>[];
      return (
        <>
          {menuItemsFiltered || listLinks ? (
            <GetLinks
              items={menuItemsFiltered ? menuItemsFiltered.concat(listLinks ?? []) : listLinks}
              changeHeaderLayout={changeHeaderLayout}
            />
          ) : null}
        </>
      );

    case "menuGroup":
      if (!isActiveEntry(item)) return null;
      assertEditorialType<Editorials.MenuGroup>(item, "menuGroup");
      return (
        <>
          {item?.fields.items || listLinks ? (
            <GetLinks
              items={item?.fields.items ? item?.fields.items?.filter(isActiveEntry).concat(listLinks ?? []) : listLinks}
              changeHeaderLayout={changeHeaderLayout}
            />
          ) : null}
        </>
      );

    default:
      return <>{listLinks ? <GetLinks items={listLinks} changeHeaderLayout={changeHeaderLayout} /> : null}</>;
  }
}
